<template>
    <v-dialog v-model="openMode" max-width="550px" content-class="settings-dialog">
      <v-card class="settings-card">
        <div class="settings-header">
          <h2 class="settings-title">Call Settings</h2>
          <p class="settings-subtitle" v-if="itemToChange.name">For {{ itemToChange.name }}</p>
        </div>
        
        <v-divider class="mx-4"></v-divider>
        
        <v-card-text class="settings-content">
          <div class="settings-sections">
            <div class="settings-section">
              <div class="section-header">
                <v-icon color="primary" class="section-icon">mdi-bell-ring</v-icon>
                <h3 class="section-title">Call Reception Permission</h3>
              </div>
              
              <div class="section-content">
                <div class="permission-status">
                  <v-checkbox 
                    v-model="itemToChange.recieve_calls" 
                    disabled 
                    hide-details
                    color="success"
                    class="permission-checkbox"
                  >
                    <template v-slot:label>
                      <span class="checkbox-label">
                        {{ itemToChange.recieve_calls ? 'Approved call reception' : 'Did not approve call reception' }}
                      </span>
                    </template>
                  </v-checkbox>
                  
                  <div v-if="itemToChange.confirm_calls_date" class="confirmation-date">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-calendar-check</v-icon>
                    Confirmation Date: {{ itemToChange.confirm_calls_date }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="settings-section">
              <div class="section-header">
                <v-icon color="primary" class="section-icon">mdi-volume-off</v-icon>
                <h3 class="section-title">Mute Settings</h3>
              </div>
              
              <div class="section-content">
                <v-checkbox 
                  v-model="itemToChange.is_muted" 
                  hide-details
                  color="primary"
                  class="mute-checkbox mb-3"
                >
                  <template v-slot:label>
                    <span class="checkbox-label">Call Muted</span>
                  </template>
                </v-checkbox>
                
                <v-expand-transition>
                  <div v-if="itemToChange.is_muted" class="mute-options">
                    <div class="mute-until-label">Set mute duration:</div>
                    
                    <div class="mute-until-field">
                      <v-text-field
                        v-model="itemToChange.mute_until"
                        type="datetime-local"
                        outlined
                        dense
                        hide-details
                        label="Mute until date"
                        prepend-inner-icon="mdi-calendar-clock"
                        :rules="[v => !itemToChange.is_muted || !!v || 'Must select an unmute date']"
                        :required="itemToChange.is_muted"
                      ></v-text-field>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </div>
        </v-card-text>
        
        <v-slide-y-transition>
          <div class="progress-container" v-if="progressShow">
            <v-progress-linear
              indeterminate
              color="primary"
              height="4"
              class="mb-3"
            ></v-progress-linear>
            <div class="progress-text">Saving settings...</div>
          </div>
        </v-slide-y-transition>
        
        <v-card-actions class="settings-actions">
          <v-btn
            text
            color="grey darken-1"
            @click="openMode = false"
            :disabled="progressShow"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          
          <v-btn
            color="primary"
            :loading="progressShow"
            :disabled="progressShow"
            @click="saveSettings"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Settings
          </v-btn>
        </v-card-actions>
      </v-card>
  
      <SnackBar 
        v-model="snackbar" 
        :snacbarText="snacbarText" 
        :snackbarColorBt="snackbarColorBt"
      ></SnackBar>
    </v-dialog>
  </template>
    
  <script>
  import ApiServices from '@/services/api.service'
  import SnackBar from '@/components/widgets/snackBar.vue'
  import Utils from '@/util/utils'
  
  export default {
    props: {
      item: Object,
      value: { type: Boolean, default: false },
    },
    components: {
      SnackBar,
    },
    data: () => ({
      progressShow: false,
      snackbar: false,
      snackbarColorBt: "green",
      snacbarText: "",
      checkbox: false,
      itemToChange: {},
    }),
  
    methods: {
      formatDate() {
        if (this.itemToChange.mute_until) {
          this.itemToChange.mute_until = Utils.getFormatDate(this.itemToChange.mute_until);
        } else {
          this.itemToChange.mute_until = "";
        }
  
        if (this.itemToChange.confirm_calls_date) {
          this.itemToChange.confirm_calls_date = Utils.getCurrentDate(this.itemToChange.confirm_calls_date);
        }
      },
      
      async saveSettings() {
        try {
          if (!this.itemToChange.is_muted) {
            this.itemToChange.mute_until = null;
          }
          
          if (this.itemToChange.is_muted && !this.itemToChange.mute_until) {
            this.$emit("itemSettingsChange", "", "Error: Must select an unmute date", "red");
            return;
          }
          
          this.progressShow = true;
          let token = localStorage.getItem("token");
          const itemsJSON = JSON.stringify({ item: this.itemToChange });
  
          let api = process.env.VUE_APP_BASE_URL + "/subscribes/update_settings";
          const res = await fetch(api, ApiServices.requestOptions("PUT", itemsJSON, token));
          const jsonObject = await res.json();
          
          if (res.status >= 400) {
            this.$emit("itemSettingsChange", "", "Error saving settings: ", "red");
          } else if (res.status === 200) {
            this.$emit("itemSettingsChange", this.itemToChange, "Settings updated successfully!", "green");
            this.openMode = false;
          }
        } catch (error) {
          this.$emit("itemSettingsChange", "", "Error saving data: " + error, "red");
        } finally {
          this.progressShow = false;
        }
      },
    },
    computed: {
      openMode: {
        get() {
          return this.value
        },
        set(v) {
          return this.$emit('input', v)
        }
      },
    },
    created() {
      this.itemToChange = { ...this.item };
      console.log(this.itemToChange);
      
      this.formatDate();
    },
  }
  </script>
  
  <style scoped>
  .settings-dialog {
    border-radius: 16px;
  }
  
  .settings-card {
    border-radius: 16px;
    overflow: hidden;
    background: linear-gradient(to bottom, #ffffff, #f8f9fa);
  }
  
  .settings-header {
    padding: 24px 24px 16px;
    text-align: center;
  }
  
  .settings-title {
    color: #0d2c6d;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 8px;
  }
  
  .settings-subtitle {
    color: #64748b;
    font-size: 16px;
  }
  
  .settings-content {
    padding: 16px 8px;
  }
  
  .settings-sections {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  
  .settings-section {
    background-color: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
  }
  
  .section-header {
    display: flex;
    align-items: center;
    padding: 16px;
    background-color: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .section-icon {
    margin-left: 12px;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }
  
  .section-content {
    padding: 16px;
  }
  
  /* Permission Status */
  .permission-status {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .confirmation-date {
    font-size: 14px;
    color: #64748b;
    display: flex;
    align-items: center;
    margin-right: 32px;
  }
  
  /* Mute Options */
  .mute-options {
    margin-top: 8px;
    padding-top: 16px;
    border-top: 1px dashed #e2e8f0;
  }
  
  .mute-until-label {
    font-size: 16px;
    font-weight: 500;
    color: #0f172a;
    margin-bottom: 12px;
  }
  
  .checkbox-label {
    font-size: 16px;
    font-weight: 500;
    color: #0f172a;
  }
  
  /* Progress */
  .progress-container {
    padding: 16px 24px;
    text-align: center;
  }
  
  .progress-text {
    font-size: 14px;
    color: #64748b;
    margin-top: 4px;
  }
  
  /* Actions */
  .settings-actions {
    padding: 16px 24px 24px;
    display: flex;
    justify-content: center;
    gap: 16px;
  }
  
  .cancel-btn, .save-btn {
    min-width: 140px;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    border-radius: 8px;
  }
  
  @media (max-width: 600px) {
    .settings-actions {
      flex-direction: column-reverse;
    }
    
    .cancel-btn, .save-btn {
      width: 100%;
    }
  }
  </style>